import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const AddPositionModal = ({ isOpen, onClose, onAddPosition }) => {
  const [formData, setFormData] = useState({
    symbol: '',
    quantity: '',
    price: '',
    orderType: 'market'
  });
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [errors, setErrors] = useState({});

  // Mock stock search data
  const mockStocks = [
    { symbol: 'AAPL', name: 'Apple Inc.', price: 175.43, sector: 'Technology' },
    { symbol: 'GOOGL', name: 'Alphabet Inc.', price: 2847.63, sector: 'Technology' },
    { symbol: 'MSFT', name: 'Microsoft Corporation', price: 378.85, sector: 'Technology' },
    { symbol: 'AMZN', name: 'Amazon.com Inc.', price: 3342.88, sector: 'Consumer Discretionary' },
    { symbol: 'TSLA', name: 'Tesla Inc.', price: 248.50, sector: 'Consumer Discretionary' },
    { symbol: 'NVDA', name: 'NVIDIA Corporation', price: 875.28, sector: 'Technology' },
    { symbol: 'META', name: 'Meta Platforms Inc.', price: 484.20, sector: 'Technology' },
    { symbol: 'NFLX', name: 'Netflix Inc.', price: 486.81, sector: 'Communication Services' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (name === 'symbol' && value.length > 0) {
      setIsSearching(true);
      // Simulate API search delay
      setTimeout(() => {
        const filtered = mockStocks.filter(stock => 
          stock.symbol.toLowerCase().includes(value.toLowerCase()) ||
          stock.name.toLowerCase().includes(value.toLowerCase())
        );
        setSearchResults(filtered.slice(0, 5));
        setIsSearching(false);
      }, 300);
    } else if (name === 'symbol' && value.length === 0) {
      setSearchResults([]);
    }
  };

  const handleStockSelect = (stock) => {
    setFormData(prev => ({
      ...prev,
      symbol: stock.symbol,
      price: formData.orderType === 'market' ? stock.price.toString() : prev.price
    }));
    setSearchResults([]);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.symbol.trim()) {
      newErrors.symbol = 'Stock symbol is required';
    }
    
    if (!formData.quantity || parseFloat(formData.quantity) <= 0) {
      newErrors.quantity = 'Quantity must be greater than 0';
    }
    
    if (!formData.price || parseFloat(formData.price) <= 0) {
      newErrors.price = 'Price must be greater than 0';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      const orderData = {
        symbol: formData.symbol.toUpperCase(),
        quantity: parseInt(formData.quantity),
        price: parseFloat(formData.price),
        orderType: formData.orderType,
        total: parseInt(formData.quantity) * parseFloat(formData.price),
        timestamp: new Date().toISOString()
      };
      
      onAddPosition(orderData);
      handleClose();
    }
  };

  const handleClose = () => {
    setFormData({ symbol: '', quantity: '', price: '', orderType: 'market' });
    setSearchResults([]);
    setErrors({});
    onClose();
  };

  const handleOrderTypeChange = (e) => {
    const { value } = e.target;
    setFormData(prev => ({ ...prev, orderType: value }));
  };

  const estimatedTotal = formData.quantity && formData.price 
    ? (parseFloat(formData.quantity) * parseFloat(formData.price)).toFixed(2)
    : '0.00';

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-modal flex items-center justify-center p-4">
      <div className="bg-card rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">Add New Position</h2>
          <Button variant="ghost" size="icon" onClick={handleClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Stock Symbol Search */}
          <div className="relative">
            <Input
              label="Stock Symbol"
              name="symbol"
              value={formData.symbol}
              onChange={handleInputChange}
              placeholder="Search for stocks (e.g., AAPL, GOOGL)"
              error={errors.symbol}
              required
            />
            
            {/* Search Results Dropdown */}
            {searchResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 bg-card border border-border rounded-lg shadow-lg z-10 mt-1">
                {searchResults.map((stock) => (
                  <button
                    key={stock.symbol}
                    type="button"
                    onClick={() => handleStockSelect(stock)}
                    className="w-full px-4 py-3 text-left hover:bg-muted transition-colors border-b border-border last:border-b-0"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-foreground">{stock.symbol}</p>
                        <p className="text-sm text-muted-foreground">{stock.name}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-mono text-foreground">₹{stock.price.toFixed(2)}</p>
                        <p className="text-xs text-muted-foreground">{stock.sector}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
            
            {isSearching && (
              <div className="absolute top-full left-0 right-0 bg-card border border-border rounded-lg shadow-lg z-10 mt-1 p-4">
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                  <span className="text-sm text-muted-foreground">Searching...</span>
                </div>
              </div>
            )}
          </div>

          {/* Order Type */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Order Type</label>
            <div className="flex space-x-4">
              <label className="flex items-center space-x-2">
                <input
                  type="radio"
                  name="orderType"
                  value="market"
                  checked={formData.orderType === 'market'}
                  onChange={handleOrderTypeChange}
                  className="text-primary focus:ring-primary"
                />
                <span className="text-sm">Market Order</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="radio"
                  name="orderType"
                  value="limit"
                  checked={formData.orderType === 'limit'}
                  onChange={handleOrderTypeChange}
                  className="text-primary focus:ring-primary"
                />
                <span className="text-sm">Limit Order</span>
              </label>
            </div>
          </div>

          {/* Quantity */}
          <Input
            label="Quantity"
            name="quantity"
            type="number"
            value={formData.quantity}
            onChange={handleInputChange}
            placeholder="Number of shares"
            error={errors.quantity}
            min="1"
            required
          />

          {/* Price */}
          <Input
            label={formData.orderType === 'market' ? 'Estimated Price' : 'Limit Price'}
            name="price"
            type="number"
            value={formData.price}
            onChange={handleInputChange}
            placeholder="Price per share"
            error={errors.price}
            min="0.01"
            step="0.01"
            required
            description={formData.orderType === 'market' ? 'Price will be determined at market execution' : 'Your maximum price per share'}
          />

          {/* Order Summary */}
          <div className="bg-muted/30 rounded-lg p-4 space-y-2">
            <h4 className="font-medium text-foreground">Order Summary</h4>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Symbol:</span>
              <span className="font-mono">{formData.symbol.toUpperCase() || '-'}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Quantity:</span>
              <span className="font-mono">{formData.quantity || '0'} shares</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Price:</span>
                              <span className="font-mono">₹{formData.price || '0.00'}</span>
            </div>
            <div className="flex justify-between text-sm font-medium border-t border-border pt-2">
              <span>Estimated Total:</span>
                              <span className="font-mono">₹{estimatedTotal}</span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-4">
            <Button variant="outline" onClick={handleClose} fullWidth>
              Cancel
            </Button>
            <Button type="submit" fullWidth>
              <Icon name="Plus" size={16} />
              <span className="ml-2">Add Position</span>
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddPositionModal;